import classNames from 'classnames'
import React from 'react'
import { Link } from 'react-router-dom'

import {
  CarNode,
  DealNode,
  DriverNode,
  InvoiceNode,
  ProposalNode,
  ReportNode,
} from '@/apollo/graphql'
import { t } from '@/i18n'

import LoadingIndicator from '@/assets/images/loading_indicator.gif'

import Button from './Button'

interface IButton {
  component?: React.ComponentType<any>
  props?: any
  className?: string
  onClick?: () => void
  children?: React.ReactNode
}

interface ISection {
  title?: string
  headerButtons?: IButton[]
  data?: {
    label: string
    value?: string | number | null | undefined
    style?: any
    link?: string | null | undefined
  }[]
  customContent?: React.ReactNode
}

interface IAboutNode {
  loading?: boolean
  onClose: () => void
  node?: Partial<
    ProposalNode | InvoiceNode | DriverNode | ReportNode | DealNode | CarNode
  >
  sections?: ISection[]
  controls?: IButton[]
  goBack?: () => void
  goBackTitle?: string
  HeaderComponent?: React.ReactNode
}

const AboutNode: React.FC<IAboutNode> = ({
  node,
  goBack,
  onClose,
  loading,
  sections,
  controls,
  goBackTitle,
  HeaderComponent,
}) => {
  return (
    <div
      className={classNames('info-box mobile-modal scroll-sticky w-100', {
        open: !!Object.keys(node || {}).length,
      })}
    >
      <div className="main-info">
        <div className="close-box">
          <Button onClick={onClose}>
            <span className="icon-close"></span>
          </Button>
        </div>
        {!!goBack && (
          <div className="prev-box">
            <Button onClick={goBack}>
              <span className="icon-arrow"></span>
              {t('backTo', { page: t(goBackTitle || '') })}
            </Button>
          </div>
        )}
        {!!HeaderComponent && HeaderComponent}
        {!loading ? (
          <>
            {sections?.map((section, idx) => (
              <div className="section-box" key={idx.toString()}>
                <div className="title-box">
                  {!!section.title && (
                    <div className="title-box">
                      <h4>{t(section.title)}</h4>
                    </div>
                  )}
                  {!!section?.headerButtons?.length && (
                    <div className="row align-items-center gap">
                      {section.headerButtons.map(
                        (
                          {
                            component: Component = Button,
                            props,
                            children,
                            ...additionalProps
                          },
                          idx,
                        ) => (
                          <Component
                            key={idx.toString()}
                            {...props}
                            {...additionalProps}
                          >
                            {children}
                          </Component>
                        ),
                      )}
                    </div>
                  )}
                </div>
                {section.customContent ? (
                  <div className="custom-content">{section.customContent}</div>
                ) : (
                  !!section.data?.length && (
                    <div className="data-list">
                      <ul>
                        {section.data.map((datum, idx) => (
                          <li key={idx.toString()}>
                            {t(datum.label)}
                            {datum.link ? (
                              <Link to={datum.link} style={datum.style}>
                                <span>{datum.value}</span>
                              </Link>
                            ) : (
                              <span style={datum.style}>
                                {datum.value ?? '-'}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                )}
              </div>
            ))}
          </>
        ) : (
          <div className="fetch-detail">
            <img className="search-loading-indicator" src={LoadingIndicator} />
          </div>
        )}
      </div>

      {!!controls?.length && (
        <div className="control-actions">
          {controls.map((control, idx) => (
            <Button
              key={idx.toString()}
              className={control.className}
              onClick={control.onClick}
            >
              {control.children}
            </Button>
          ))}
        </div>
      )}
    </div>
  )
}

export default AboutNode
